import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, AlertCircle, Truck, Building2 } from 'lucide-react';

const ImageSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const slides = [
    {
      image: '/images/banner1.jpeg',
      title: 'Every Drop Saves Lives',
      subtitle: "India's Premier Blood Donation Platform",
      description: 'Connect with emergency blood requests and verified donors across India. 24/7 support for all critical needs.',
    },
    {
      image: '/images/banner2.jpeg',
      title: 'Be a Hero Today',
      subtitle: "Make a Lasting Difference",
      description: 'Your simple decision to donate blood can save up to three lives. Join thousands of lifesavers.',
    },
    {
      image: '/images/banner3.jpeg',
      title: 'Emergency Network',
      subtitle: "Fast & Reliable Coordination",
      description: 'Get connected with emergency vehicles and hospitals in real-time when seconds count.',
    },
    {
      image: '/images/banner4.jpeg',
      title: 'Support Campaigns',
      subtitle: "Join Hands For Good",
      description: 'Contribute to blood camps and community healthcare projects run by verified partner NGOs.',
    },
  ];

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4500);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    if (!isHovered) {
      startTimer();
    } else {
      stopTimer();
    }
    return () => stopTimer();
  }, [isHovered]);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleDotClick = (index, e) => {
    e.stopPropagation();
    setCurrentSlide(index);
  };

  return (
    <div 
      className="relative w-full h-[75vh] sm:h-[80vh] md:h-[85vh] lg:h-[80vh] overflow-hidden bg-gray-950 select-none group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slides container */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
          style={{
            backgroundImage: `url(${slide.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Overlay with rich color gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-900/60 to-black/40" />
          
          {/* Content overlay */}
          <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6 md:p-10">
            <div className="max-w-4xl w-full text-center text-white px-2">
              {/* Subtitle */}
              <p className="text-red-500 font-semibold text-xs sm:text-sm md:text-base tracking-widest uppercase mb-2 md:mb-3 animate-fade-in-down">
                {slide.subtitle}
              </p>
              
              {/* Main Title */}
              <h2 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-extrabold mb-3 md:mb-5 tracking-tight leading-tight">
                {slide.title.split(' ').map((word, wIdx, arr) => 
                  wIdx === arr.length - 1 ? (
                    <span key={wIdx} className="text-red-500 bg-clip-text drop-shadow-sm">{word}</span>
                  ) : (
                    word + ' '
                  )
                )}
              </h2>
              
              {/* Description */}
              <p className="text-gray-300 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-6 md:mb-8 font-light leading-relaxed">
                {slide.description}
              </p>
              
              {/* Responsive Action Buttons Container */}
              <div className="w-full max-w-2xl mx-auto">
                {/* Primary Actions Grid */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <button
                    onClick={() => navigate('/emergency-blood')}
                    className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 shadow-lg hover:shadow-red-900/40"
                  >
                    <Heart className="w-3.5 h-3.5 sm:w-5 sm:h-5 fill-current" />
                    <span>Donate Blood</span>
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3.5 bg-transparent border-2 border-white hover:bg-white hover:text-gray-950 text-white font-bold rounded-xl text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 shadow-lg"
                  >
                    <AlertCircle className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                    <span className="truncate">Emergency Request</span>
                  </button>
                </div>
                
                {/* Secondary Actions Grid */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <button
                    onClick={() => navigate('/vehicle/register')}
                    className="flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2.5 bg-emerald-600/90 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs sm:text-sm transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 shadow-md"
                  >
                    <Truck className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5" />
                    <span className="truncate">Register Driver</span>
                  </button>
                  <button
                    onClick={() => navigate('/register-hospital')}
                    className="flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2.5 bg-blue-600/90 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs sm:text-sm transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 shadow-md"
                  >
                    <Building2 className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5" />
                    <span className="truncate">Register Hospital</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows - hidden on mobile, visible on desktop hover */}
      <button
        onClick={handlePrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 hover:bg-red-600/90 text-white border border-white/10 backdrop-blur-sm transition-all duration-300 opacity-0 md:group-hover:opacity-100 hidden md:flex"
        aria-label="Previous Slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={handleNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 hover:bg-red-600/90 text-white border border-white/10 backdrop-blur-sm transition-all duration-300 opacity-0 md:group-hover:opacity-100 hidden md:flex"
        aria-label="Next Slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Slide Indicators / Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2.5">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={(e) => handleDotClick(index, e)}
            className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide ? 'bg-red-500 w-6 sm:w-8' : 'bg-white/40 w-2 sm:w-2.5 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageSlider;
